// @flow

import queryString from 'query-string';

/**
 * Ad tracking click ID capture and retrieval.
 * Captures click IDs (fbclid, future gclid) from URL params
 * and stores them in localStorage for later use during registration.
 */

const CLICK_ID_KEYS: { [string]: string } = {
  fbclid: 'neo_fbclid',
  gclid: 'neo_gclid',
};

const captureClickIds = () => {
  if (typeof window === 'undefined') return;
  const parsed = queryString.parse(window.location.search);
  Object.entries(CLICK_ID_KEYS).forEach(([paramKey, storageKey]) => {
    const value = parsed[paramKey];
    if (value) {
      window.localStorage.setItem(storageKey, String(value));
    }
  });
};

const getClickId = (paramKey: string): ?string => {
  const storageKey = CLICK_ID_KEYS[paramKey];
  return storageKey ? window.localStorage.getItem(storageKey) : null;
};

const getAllClickIds = (): { [string]: string } => {
  const result = {};
  Object.entries(CLICK_ID_KEYS).forEach(([paramKey, storageKey]) => {
    const value = window.localStorage.getItem(String(storageKey));
    if (value) {
      result[paramKey] = value;
    }
  });
  return result;
};

/**
 * Facebook Pixel event tracking.
 * Fires standard and custom events via the fbq() global
 * initialized in public/index.html.
 */

const trackEvent = (eventName: string, params?: Object) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('track', eventName, params);
  }
};

const trackCustomEvent = (eventName: string, params?: Object) => {
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('trackCustom', eventName, params);
  }
};

/**
 * Google Ads conversion tracking.
 * Fires conversion events via the gtag() global
 * initialized in public/index.html.
 */

const GOOGLE_ADS_CONVERSION_ID = 'AW-18023132280';

// One Google Ads conversion key per funnel stage. Names use the
// `SignupStep1` / `SignupStep2V2` / `SignupLegacy` shape to keep the V2
// 2-step `/signup` flow and the 1-step legacy `/register` flow
// independently attributable in Google Ads. See marketing naming spec
// in .claude/tasks/context_session_89_signup_step_tracking.md.
type GoogleConversionKey =
  | 'SignupLegacy'
  | 'SignupStep1'
  | 'SignupStep2V2'
  | 'CompleteRegistration'
  | 'InitiateCheckout'
  | 'StartTrial'
  | 'Purchase';

const GOOGLE_ADS_LABELS: { [GoogleConversionKey]: string } = {
  // Existing conversion action — fires from the legacy 1-step `/register` flow.
  // Marketing should rename this action in Google Ads UI to
  // `01 · Signup submitted · legacy /register`. The 10-char label below is
  // unchanged so this code keeps working through the rename.
  SignupLegacy: 'zaUOCICi9pocEPjYjJJD',
  // TODO(marketing): create `01a · Signup step 1 · V2 /signup` in Google Ads
  // (category Lead, counting One) and paste the 10-char label here.
  // Until set, the gtag half of step 1 no-ops (FB Pixel half still fires).
  SignupStep1: '',
  // TODO(marketing): create `01b · Signup step 2 · V2 /signup` in Google Ads
  // (category Lead, counting One) and paste the 10-char label here.
  // Until set, the gtag half of V2 step 2 no-ops (FB Pixel half still fires).
  SignupStep2V2: '',
  CompleteRegistration: 'TPYeCIOi9pocEPjYjJJD',
  InitiateCheckout: 'A43CCIai9pocEPjYjJJD',
  StartTrial: '_i8YCP3WjpscEPjYjJJD',
  Purchase: 'LKU0CIDXjpscEPjYjJJD',
};

const trackGoogleConversion = (eventKey: GoogleConversionKey, params?: Object) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    const label = GOOGLE_ADS_LABELS[eventKey];
    if (label) {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_CONVERSION_ID}/${label}`,
        ...params
      });
    } else if (process.env.NODE_ENV !== 'production') {
      // Flow types are erased at runtime, so this guards both typo'd keys
      // and known keys whose label hasn't been configured yet.
      const reason = Object.prototype.hasOwnProperty.call(GOOGLE_ADS_LABELS, eventKey)
        ? 'label not configured (set GOOGLE_ADS_LABELS in adTracking.js)'
        : 'unknown eventKey';
      // eslint-disable-next-line no-console
      console.warn('[adTracking][trackGoogleConversion]', reason, ':', eventKey);
    }
  }
};

/**
 * Marketing-owned Zapier catch hook for step-1 signup leads. Posts the
 * minimal lead profile the user has provided by the time the `Lead` event
 * fires so Marketing can route into their existing automations (Sheets +
 * outbound email). Step-2-only fields (`password`, company `website`) are
 * intentionally not included here — see [[TASK_signup_step1_zapier_webhook]].
 */
const ZAPIER_SIGNUP_STEP1_WEBHOOK = 'https://hooks.zapier.com/hooks/catch/8765095/uvmatq3/';

const sendSignupStep1Webhook = (lead: { name: ?string, email: ?string, phone: ?string }) => {
  // Only fire in production — dev/staging/local signups would otherwise
  // pollute Marketing's production Sheet + outbound-email automations.
  // Project convention: REACT_APP_ENVF is set to 'env.production' only in
  // the prod build (.env.production). See UserProfileMenu.js for the same gate.
  if (process.env.REACT_APP_ENVF !== 'env.production') return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  const payload = {
    event: 'Lead',
    name: lead.name || '',
    email: lead.email || '',
    phone: lead.phone || '',
    timestamp: new Date().toISOString(),
    source: window.location.href,
    querystring: (window.location.search || '').replace(/^\?/, '')
  };
  // text/plain avoids a CORS preflight; Zapier parses JSON from text/plain
  // bodies. keepalive lets the request survive a page navigation. Errors are
  // swallowed — a Zap outage must never block the user from continuing.
  try {
    window.fetch(ZAPIER_SIGNUP_STEP1_WEBHOOK, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(payload),
      keepalive: true
    }).catch(() => {});
  } catch (e) {
    // Swallow synchronous fetch failures (e.g. malformed URL in tests).
  }
};

/**
 * Per-step signup funnel tracking for the V2 `/signup` flow. Fires one FB
 * Pixel `Lead` event (with a step-specific `content_name`) + one Google Ads
 * conversion per step so the two steps show up as distinct levels in Ads
 * Manager. Use `trackLegacySignup` for the 1-step `/register` flow.
 * Callers MUST dedup per page session (e.g. with a useRef guard) —
 * this helper has no internal dedup.
 */
const trackSignupStep = (step: 1 | 2) => {
  if (step === 1) {
    // Standard `Lead` only on step 1: step 1 is the highest-volume signal
    // in the funnel, so Meta's ad-objective ML gets its primary `Lead`
    // training signal here. Step 2 and legacy completion use custom events
    // (auto-surfaced as their own rows in Events Manager).
    trackEvent('Lead', { content_name: 'signup_v2_step1' });
    trackGoogleConversion('SignupStep1');
    return;
  }
  if (step === 2) {
    trackCustomEvent('SignupLeadV2');
    trackGoogleConversion('SignupStep2V2');
    return;
  }
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('[adTracking][trackSignupStep] unknown step:', step);
  }
};

/**
 * Legacy `/register` (1-step) signup tracking. Uses a custom event so the
 * legacy and V2 flows are independently attributable in Meta Events Manager
 * (each event name auto-surfaces as its own row — no Custom Conversion setup
 * needed) and in Google Ads (via dedicated conversion actions).
 */
const trackLegacySignup = () => {
  trackCustomEvent('SignupLeadLegacy');
  trackGoogleConversion('SignupLegacy');
};

/**
 * Field-level interaction tracking for the signup form.
 * Fires a gtag event (routed to GA4 once a G- measurement ID is
 * configured in index.html) and a Facebook Pixel custom event so
 * the signal is immediately visible in Meta Events Manager funnels.
 * Used to surface which field the user last interacted with before
 * abandoning — i.e. registration drop-off diagnostics.
 *
 * Callers MUST dedup per-session — this fires on every call.
 */
const trackFieldFocus = (fieldName: string) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', 'signup_field_focus', {
      event_category: 'signup',
      field_name: fieldName
    });
  }
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    window.fbq('trackCustom', 'SignupFieldFocus', { field_name: fieldName });
  }
};

export default {
  captureClickIds,
  getClickId,
  getAllClickIds,
  trackEvent,
  trackCustomEvent,
  trackGoogleConversion,
  trackSignupStep,
  trackLegacySignup,
  trackFieldFocus,
  sendSignupStep1Webhook
};
